/**
 * Helpers for the department organization features (WS-F, workspace
 * docs/guardian/06, 2026-10-23). All values here originate from the Backend or
 * third parties (NWS / WFIGS / FIRMS) and are treated as untrusted: times are
 * parsed strictly, alert links are only honoured when they are absolute https
 * URLs, and every label falls back to the raw value rendered as text.
 */
import { safeStorage } from './safeStorage';

/** Human labels for the alert data source. Unknown sources render verbatim. */
export const ALERT_SOURCE_LABEL = {
  nws: 'National Weather Service',
  wfigs: 'Wildland fire (WFIGS)',
  firms: 'Satellite detection (FIRMS)',
};

export function alertSourceLabel(source) {
  if (typeof source !== 'string') return 'Alert';
  return ALERT_SOURCE_LABEL[source] || source;
}

/**
 * Parses an ISO timestamp and formats it for display, or returns null when the
 * value is missing or unparseable (never throws, never shows "Invalid Date").
 */
export function formatDeptTime(iso) {
  if (typeof iso !== 'string' || iso.length === 0) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  try {
    return new Date(ms).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return new Date(ms).toISOString();
  }
}

/**
 * Returns the URL only when it is an absolute https link; anything else (http,
 * javascript:, relative, malformed) returns null so the UI renders no link.
 */
export function safeAlertUrl(url) {
  if (typeof url !== 'string' || url.length === 0) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}

/** The millisecond timestamp of the most recent alert in a list (0 when none). */
export function newestAlertAt(alerts) {
  if (!Array.isArray(alerts)) return 0;
  let newest = 0;
  for (const a of alerts) {
    const ms = Date.parse(a?.detectedAt || '');
    if (!Number.isNaN(ms) && ms > newest) newest = ms;
  }
  return newest;
}

const SEEN_PREFIX = 'guardian.deptAlertsSeen.';

function seenKey(userId) {
  return `${SEEN_PREFIX}${userId || 'anon'}`;
}

/** Records that the member has seen alerts up to `ms` (the newest they viewed). */
export function markAlertsSeen(userId, ms) {
  if (!ms || Number.isNaN(ms)) return;
  const prev = Number(safeStorage.get(seenKey(userId))) || 0;
  if (ms > prev) safeStorage.set(seenKey(userId), String(ms));
}

/** True when `newestMs` is newer than the last time the member viewed alerts. */
export function hasUnseenAlerts(userId, newestMs) {
  if (!newestMs) return false;
  const seen = Number(safeStorage.get(seenKey(userId))) || 0;
  return newestMs > seen;
}
